/**
 * @module bridges
 * @package @geenius/adapters/react-mui
 * @description MUI-native bridge components for adapter loading, error, and
 * notification surfaces. Adapter contracts stay delegated to the React
 * reference variant; this module only owns MUI rendering affordances.
 */

"use client";

import {
  Alert,
  type AlertColor,
  type AlertProps,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  type CircularProgressProps,
  LinearProgress,
  type LinearProgressProps,
  Snackbar,
  type SnackbarProps,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from "@mui/material";
import { Component, type ErrorInfo, type JSX, type ReactNode } from "react";

const DEFAULT_TOAST_ANCHOR: SnackbarProps["anchorOrigin"] = {
  vertical: "bottom",
  horizontal: "center",
};

function normalizeProgress(progress: number | undefined): number | undefined {
  if (progress === undefined) {
    return undefined;
  }

  return Math.min(100, Math.max(0, progress));
}

/**
 * Props accepted by the MUI async-adapter loading bridge.
 *
 * @property loading Controls whether the loading affordance or children render.
 * @property isLoading Alias for `loading` used by async adapter states.
 * @property label Accessible and visible loading label.
 * @property progress Optional determinate progress value from 0 through 100.
 * @property variant Chooses MUI circular or linear progress primitives.
 * @property children Content rendered when loading is explicitly false.
 * @property sx Optional MUI system styles for the bridge wrapper.
 * @property progressProps Props forwarded to the selected MUI progress primitive.
 */
export interface LoadingBridgeProps {
  loading?: boolean;
  isLoading?: boolean;
  label?: ReactNode;
  progress?: number;
  variant?: "circular" | "linear";
  children?: ReactNode;
  sx?: SxProps<Theme>;
  progressProps?: CircularProgressProps | LinearProgressProps;
}

/**
 * Renders a MUI loading affordance for async adapter operations.
 *
 * @param props Loading bridge props.
 * @returns Children when idle, otherwise an accessible progress indicator.
 */
export function LoadingBridge({
  loading,
  isLoading,
  label = "Loading adapters",
  progress,
  variant = "circular",
  children = null,
  sx,
  progressProps,
}: LoadingBridgeProps): JSX.Element {
  const active = isLoading ?? loading ?? true;

  if (!active) {
    return <>{children}</>;
  }

  const normalizedProgress = normalizeProgress(progress);
  const progressVariant =
    normalizedProgress === undefined ? "indeterminate" : "determinate";
  const progressLabel = typeof label === "string" ? label : "Loading adapters";

  return (
    <Stack
      role="status"
      aria-live="polite"
      aria-busy="true"
      spacing={1.5}
      sx={[
        {
          alignItems: variant === "linear" ? "stretch" : "center",
          minWidth: variant === "linear" ? 240 : undefined,
          py: 2,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {variant === "linear" ? (
        <LinearProgress
          {...(progressProps as LinearProgressProps | undefined)}
          aria-label={progressLabel}
          value={normalizedProgress}
          variant={progressVariant}
        />
      ) : (
        <CircularProgress
          {...(progressProps as CircularProgressProps | undefined)}
          aria-label={progressLabel}
          value={normalizedProgress}
          variant={progressVariant}
        />
      )}
      {label ? (
        <Typography color="text.secondary" component="span" variant="body2">
          {label}
        </Typography>
      ) : null}
    </Stack>
  );
}

type ErrorBoundaryFallback =
  | ReactNode
  | ((error: Error, reset: () => void) => ReactNode);

/**
 * Props accepted by the MUI adapter error boundary bridge.
 *
 * @property children React subtree protected by the boundary.
 * @property error Optional controlled error for async adapter workflows.
 * @property fallback Optional custom fallback renderer or node.
 * @property onError Callback invoked with the caught error and React error info.
 * @property onReset Called before local error state is reset.
 * @property title Heading displayed in the default MUI alert fallback.
 * @property description Supporting copy displayed in the default fallback.
 * @property resetLabel Button label for retrying the protected subtree.
 * @property sx Optional MUI system styles for the default fallback wrapper.
 */
export interface ErrorBoundaryBridgeProps {
  children?: ReactNode;
  error?: unknown;
  fallback?: ErrorBoundaryFallback;
  onError?: (error: Error, info: ErrorInfo) => void;
  onReset?: () => void;
  title?: ReactNode;
  description?: ReactNode;
  resetLabel?: ReactNode;
  sx?: SxProps<Theme>;
}

interface ErrorBoundaryBridgeState {
  caughtError: Error | null;
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  return new Error(typeof error === "string" ? error : "Adapter bridge error");
}

/**
 * Catches render-time failures and displays a MUI-native adapter error state.
 */
export class ErrorBoundaryBridge extends Component<
  ErrorBoundaryBridgeProps,
  ErrorBoundaryBridgeState
> {
  state: ErrorBoundaryBridgeState = { caughtError: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryBridgeState {
    return { caughtError: normalizeError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(normalizeError(error), info);
  }

  private reset = (): void => {
    this.props.onReset?.();
    this.setState({ caughtError: null });
  };

  render(): ReactNode {
    const error =
      this.props.error !== undefined
        ? normalizeError(this.props.error)
        : this.state.caughtError;

    if (!error) {
      return this.props.children;
    }

    if (typeof this.props.fallback === "function") {
      return this.props.fallback(error, this.reset);
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <Box sx={this.props.sx}>
        <Alert
          action={
            <Button color="inherit" onClick={this.reset} size="small">
              {this.props.resetLabel ?? "Retry"}
            </Button>
          }
          severity="error"
          variant="outlined"
        >
          <AlertTitle>{this.props.title ?? "Adapter error"}</AlertTitle>
          {this.props.description ? (
            <Typography component="p" sx={{ mb: 0.75 }} variant="body2">
              {this.props.description}
            </Typography>
          ) : null}
          <Typography component="span" variant="body2">
            {error.message}
          </Typography>
        </Alert>
      </Box>
    );
  }
}

/**
 * Props accepted by the MUI telemetry/notification toast bridge.
 *
 * @property open Whether the toast is visible.
 * @property message Message rendered inside the MUI alert.
 * @property severity MUI alert severity for the toast.
 * @property title Optional alert title.
 * @property onClose MUI Snackbar close callback.
 * @property autoHideDuration Milliseconds before auto-close.
 * @property anchorOrigin Snackbar anchor position.
 * @property action Optional alert action node.
 * @property snackbarProps Additional Snackbar props that do not replace bridge-controlled props.
 * @property alertProps Additional Alert props that do not replace bridge-controlled props.
 * @property sx Optional MUI system styles for the Snackbar wrapper.
 */
export interface ToastBridgeProps {
  open?: boolean;
  message?: ReactNode;
  severity?: AlertColor;
  title?: ReactNode;
  onClose?: SnackbarProps["onClose"];
  autoHideDuration?: SnackbarProps["autoHideDuration"];
  anchorOrigin?: SnackbarProps["anchorOrigin"];
  action?: ReactNode;
  children?: ReactNode;
  snackbarProps?: Omit<
    SnackbarProps,
    | "anchorOrigin"
    | "autoHideDuration"
    | "children"
    | "message"
    | "onClose"
    | "open"
    | "sx"
  >;
  alertProps?: Omit<AlertProps, "action" | "children" | "severity">;
  sx?: SxProps<Theme>;
}

/**
 * Renders adapter telemetry or notification messages through MUI Snackbar.
 *
 * @param props Toast bridge props.
 * @returns A MUI Snackbar containing a severity-aware Alert.
 */
export function ToastBridge({
  open = true,
  message,
  severity = "info",
  title,
  onClose,
  autoHideDuration = 6000,
  anchorOrigin = DEFAULT_TOAST_ANCHOR,
  action,
  children,
  snackbarProps,
  alertProps,
  sx,
}: ToastBridgeProps): JSX.Element {
  return (
    <Snackbar
      {...snackbarProps}
      anchorOrigin={anchorOrigin}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      open={open}
      sx={sx}
    >
      <Alert
        {...alertProps}
        action={action}
        severity={severity}
        variant={alertProps?.variant ?? "filled"}
      >
        {title ? <AlertTitle>{title}</AlertTitle> : null}
        {message}
        {children}
      </Alert>
    </Snackbar>
  );
}
